// Detects errors caused by deployment version skew — i.e. the running client
// loaded JS chunks or Server Actions from a deployment that has since been
// replaced (a new merge to main shipped while the operator was mid-task). These
// are NOT real bugs: the client is simply running an older build than what is
// now live.
//
// Vercel Skew Protection prevents almost all of these at the platform level (it
// pins each client to the deployment it originally loaded, for a window). This
// helper is the belt-and-suspenders for the rare out-of-window case: it lets the
// error boundaries and BuildWatcher surface a calm "nueva versión" prompt
// instead of the alarming "Tuvimos un problema" crash card — never wiping an
// in-progress order. See components/VersionUpdateScreen.tsx and
// components/BuildWatcher.tsx.

// Each pattern is specific enough that a genuine application bug won't match.
const SKEW_MESSAGE_PATTERNS: RegExp[] = [
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i, // webpack
  /Loading CSS chunk/i, // webpack CSS
  /Failed to fetch dynamically imported module/i, // ESM (Turbopack / modern bundlers)
  /error loading dynamically imported module/i,
  /Importing a module script failed/i, // Safari ESM dynamic import
  /Failed to load module script/i, // Chrome ESM MIME mismatch
  /is not a valid JavaScript MIME type/i, // chunk 404 served the HTML fallback
  /Failed to find Server Action/i, // Server Action skew — server-side phrasing
  // SESS-15: what the CLIENT actually throws in Next 16 is UnrecognizedActionError
  // with this phrasing (server-action-reducer.js) — the server-side string above
  // never reaches the browser on the fetch-action path. Kept narrow (quoted id)
  // so an app-level "X was not found on the server" can't false-positive.
  /Server Action "[^"]*" was not found on the server/i,
  /failed-to-find-server-action/i, // docs URL embedded in both variants
]

function readField(error: unknown, field: "name" | "message"): string {
  if (typeof error === "object" && error !== null && field in error) {
    const value = (error as Record<string, unknown>)[field]
    return typeof value === "string" ? value : ""
  }
  return ""
}

export function isVersionSkewError(error: unknown): boolean {
  if (!error) return false

  // webpack names its failed-chunk error "ChunkLoadError".
  if (readField(error, "name") === "ChunkLoadError") return true

  // Next 16 client-side Server Action skew (thrown by server-action-reducer
  // when the live deployment no longer recognizes the action id).
  if (readField(error, "name") === "UnrecognizedActionError") return true

  const message =
    typeof error === "string" ? error : readField(error, "message")
  if (!message) return false

  return SKEW_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
}

// SESS-15: skew errors swallowed by a local try/catch (PinGate's PIN submit,
// PosReadTokenProvider's mint) never reach window "error"/"unhandledrejection",
// so BuildWatcher stayed blind exactly where operators hit skew the hardest —
// the night of 2026-07-17 they read it as "the tablet broke" and wiped app
// data, killing perfectly healthy sessions. Components that catch their own
// errors call this instead; BuildWatcher listens and shows its update prompt.
export const VERSION_SKEW_EVENT = "redy:version-skew"

/** Returns true (and notifies BuildWatcher) if the caught error is version skew. */
export function reportCaughtVersionSkew(error: unknown): boolean {
  if (!isVersionSkewError(error)) return false
  if (typeof window !== "undefined") window.dispatchEvent(new Event(VERSION_SKEW_EVENT))
  return true
}
